import { FontIcon, Stack, Text } from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'
import DOMPurify from 'dompurify'
import { FormEvent, memo, useContext, useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'
import remarkBreaks from 'remark-breaks'
import { AskResponse, Citation, FeedbackType, historyMessageFeedback } from '../../api'
import { XSSAllowAttributes, XSSAllowTags } from '../../constants/sanatizeAllowables'
import { AppStateContext } from '../../state/AppProvider'
import supersub from './remarkSafeSuperSub'

import Check from '../../assets/check.svg'
import Copy from '../../assets/copy.svg'
import ThumbDown from '../../assets/thumb_down.svg'
import ThumbUp from '../../assets/thumb_up.svg'

import { parseAnswer } from './AnswerParser'

import { ErrorOutlineOutlined, ForumOutlined } from '@mui/icons-material'
import { Checkbox, FormControlLabel, FormGroup, TextField } from '@mui/material'
import { includes, isEmpty, split } from 'lodash'
import rehypeSanitize from 'rehype-sanitize'
import { math } from '@streamdown/math'
import 'katex/dist/katex.min.css'
import { Streamdown, defaultRemarkPlugins } from 'streamdown'
import CustomDialog from '../dialogs/CustomDialog/CustomDialog'
import styles from './Answer.module.css'
interface Props {
  answer: AskResponse
  onCitationClicked: (citedDocument: Citation) => void
  onQuestionClicked?: (question: string) => void
}

interface UnhelpfulFeedbackContentProps {
  negativeFeedbackList: FeedbackType[]
  feedbackText: string
  setFeedbackText: (text: string) => void
  updateFeedbackList: (ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void
}

const UnhelpfulFeedbackContent = memo(
  ({ negativeFeedbackList = [], feedbackText, setFeedbackText, updateFeedbackList }: UnhelpfulFeedbackContentProps) => {
    return (
      <>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                id={FeedbackType.MissingCitation}
                checked={negativeFeedbackList.includes(FeedbackType.MissingCitation)}
                onChange={updateFeedbackList}
                className={styles.checkbox}
              />
            }
            label="Citations are missing"
          />
          <FormControlLabel
            control={
              <Checkbox
                id={FeedbackType.WrongCitation}
                checked={negativeFeedbackList.includes(FeedbackType.WrongCitation)}
                onChange={updateFeedbackList}
                className={styles.checkbox}
              />
            }
            label="Citations are wrong"
          />
          <FormControlLabel
            control={
              <Checkbox
                id={FeedbackType.OutOfScope}
                checked={negativeFeedbackList.includes(FeedbackType.OutOfScope)}
                onChange={updateFeedbackList}
                className={styles.checkbox}
              />
            }
            label="The response is not from my data"
          />
          <FormControlLabel
            control={
              <Checkbox
                id={FeedbackType.InaccurateOrIrrelevant}
                checked={negativeFeedbackList.includes(FeedbackType.InaccurateOrIrrelevant)}
                onChange={updateFeedbackList}
                className={styles.checkbox}
              />
            }
            label="Inaccurate or irrelevant"
          />
          <FormControlLabel
            control={
              <Checkbox
                id={FeedbackType.OtherUnhelpful}
                checked={negativeFeedbackList.includes(FeedbackType.OtherUnhelpful)}
                onChange={updateFeedbackList}
                className={styles.checkbox}
              />
            }
            label="Other"
          />
        </FormGroup>
        <div className={styles.feedbackTextContainer}>
          <p>Your opinion matters! Provide more details about your feedback:</p>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={feedbackText}
            onChange={e => {
              setFeedbackText(e.target.value)
            }}
            slotProps={{ htmlInput: { maxLength: 500 } }}
            helperText={`${500 - feedbackText.length} characters remaining`}
          />
        </div>
      </>
    )
  },
)

export const Answer = ({ answer, onCitationClicked, onQuestionClicked }: Props) => {
  const initializeAnswerFeedback = (answer: AskResponse) => {
    if (answer.message_id == undefined) return undefined
    if (answer.feedback == undefined) return undefined
    if (split(answer.feedback.type, ',').length > 1) return FeedbackType.Negative
    if (includes(Object.values(FeedbackType), answer.feedback.type)) return answer.feedback.type
    return FeedbackType.Neutral
  }

  const isError = !isEmpty(answer.error)
  const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] = useBoolean(false)
  const filePathTruncationLimit = 50

  const parsedAnswer = useMemo(() => parseAnswer(answer), [answer])
  const [chevronIsExpanded, setChevronIsExpanded] = useState(isRefAccordionOpen)
  const [feedbackState, setFeedbackState] = useState(initializeAnswerFeedback(answer))
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false)
  const [negativeFeedbackList, setNegativeFeedbackList] = useState<FeedbackType[]>([])
  const [feedbackText, setFeedbackText] = useState('')
  const [isCopied, setIsCopied] = useState<boolean>(false)
  const appStateContext = useContext(AppStateContext)
  const FEEDBACK_ENABLED =
    appStateContext?.state.frontendSettings?.feedback_enabled && appStateContext?.state.isCosmosDBAvailable?.cosmosDB
  const SANITIZE_ANSWER = appStateContext?.state.frontendSettings?.sanitize_answer
  const questions = answer.sections?.questions
  const sources = answer.sections?.sources

  const handleChevronClick = () => {
    setChevronIsExpanded(!chevronIsExpanded)
    toggleIsRefAccordionOpen()
  }

  useEffect(() => {
    setChevronIsExpanded(isRefAccordionOpen)
  }, [isRefAccordionOpen])

  useEffect(() => {
    if (answer.message_id == undefined) return

    let currentFeedbackState
    if (appStateContext?.state.feedbackState && appStateContext?.state.feedbackState[answer.message_id]) {
      currentFeedbackState = appStateContext?.state.feedbackState[answer.message_id]
    } else {
      currentFeedbackState = initializeAnswerFeedback(answer)
    }
    setFeedbackState(currentFeedbackState)
  }, [appStateContext?.state.feedbackState, feedbackState, answer.message_id])

  const createCitationFilepath = (citation: Citation, index: number, truncate: boolean = false) => {
    let citationFilename = ''

    if (citation.filepath) {
      const part_i = citation.part_index ?? (citation.chunk_id ? parseInt(citation.chunk_id) + 1 : '')
      if (truncate && citation.filepath.length > filePathTruncationLimit) {
        const citationLength = citation.filepath.length
        citationFilename = `${citation.filepath.substring(0, 20)}...${citation.filepath.substring(citationLength - 20)} - Part ${part_i}`
      } else {
        citationFilename = `${citation.filepath} - Part ${part_i}`
      }
    } else if (citation.filepath && citation.reindex_id) {
      citationFilename = `${citation.filepath} - Part ${citation.reindex_id}`
    } else {
      citationFilename = `Citation ${index}`
    }
    return citationFilename
  }

  const onLikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    // Set or unset the thumbs up state
    if (feedbackState == FeedbackType.Positive) {
      newFeedbackState = FeedbackType.Neutral
    } else {
      newFeedbackState = FeedbackType.Positive
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState },
    })
    setFeedbackState(newFeedbackState)

    // Update message feedback in db
    await historyMessageFeedback(answer.message_id, {
      type: newFeedbackState,
      details: undefined,
    })
  }

  const onDislikeResponseClicked = async () => {
    if (answer.message_id == undefined) return

    let newFeedbackState = feedbackState
    if (
      feedbackState === undefined ||
      feedbackState === FeedbackType.Neutral ||
      feedbackState === FeedbackType.Positive
    ) {
      newFeedbackState = FeedbackType.Negative
      setFeedbackState(newFeedbackState)
      setIsFeedbackDialogOpen(true)
    } else {
      // Reset negative feedback to neutral
      newFeedbackState = FeedbackType.Neutral
      setFeedbackState(newFeedbackState)
      await historyMessageFeedback(answer.message_id, {
        type: FeedbackType.Neutral,
        details: undefined,
      })
    }
    appStateContext?.dispatch({
      type: 'SET_FEEDBACK_STATE',
      payload: { answerId: answer.message_id, feedback: newFeedbackState },
    })
  }

  const updateFeedbackList = (ev?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    if (answer.message_id == undefined) return
    const selectedFeedback = (ev?.target as HTMLInputElement)?.id as FeedbackType

    let feedbackList = negativeFeedbackList.slice()
    if (checked) {
      feedbackList.push(selectedFeedback)
    } else {
      feedbackList = feedbackList.filter(f => f !== selectedFeedback)
    }

    setNegativeFeedbackList(feedbackList)
  }

  const onSubmitNegativeFeedback = async () => {
    if (answer.message_id == undefined) return
    await historyMessageFeedback(answer.message_id, {
      type: negativeFeedbackList.join(','),
      details: feedbackText,
    })
    resetFeedbackDialog()
  }

  const resetFeedbackDialog = () => {
    setIsFeedbackDialogOpen(false)
    setNegativeFeedbackList([])
    setFeedbackText('')
  }

  useEffect(() => {
    if (!isCopied) return

    const timeoutId = setTimeout(() => {
      setIsCopied(false)
    }, 3000)

    return () => clearTimeout(timeoutId)
  }, [isCopied])

  const onCopyResponseClicked = async () => {
    if (!answer.answer) return
    if (typeof answer.answer !== 'string') return
    if (isCopied) return

    try {
      await navigator.clipboard.writeText(answer.answer)
      setIsCopied(true)

      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Copied to clipboard', severity: 'success' },
      })
    } catch (error) {
      console.error('Failed to copy text: ', error)
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Failed to copy to clipboard', severity: 'warning' },
      })
    }
  }

  const components = {
    code({ node, ...props }: { node?: any; [key: string]: any }) {
      const isInline = !props.className && node?.children?.length === 1 && node.children[0].type === 'text' && !node.children[0].value.includes('\n')
      if (isInline) {
        return <code {...props} />
      }
      let language
      if (props.className) {
        const match = props.className.match(/language-(\w+)/)
        language = match ? match[1] : undefined
      }
      const codeString = node.children[0].value ?? ''
      return (
        <SyntaxHighlighter
          style={nord}
          language={language}
          PreTag="div"
          {...props}>
          {codeString}
        </SyntaxHighlighter>
      )
    },
    table({ node, ...props }: { node?: any; [key: string]: any }) {
      return (
        <div className={styles.tableWrapper}>
          <div className={styles.tableScroll}>
            <table {...props} />
          </div>
        </div>
      )
    },
    thead({ node, ...props }: { node?: any; [key: string]: any }) {
      return <thead {...props} />
    },
    tbody({ node, ...props }: { node?: any; [key: string]: any }) {
      return <tbody {...props} />
    },
    tr({ node, ...props }: { node?: any; [key: string]: any }) {
      return <tr {...props} />
    },
    th({ node, ...props }: { node?: any; [key: string]: any }) {
      return <th {...props} />
    },
    td({ node, ...props }: { node?: any; [key: string]: any }) {
      return <td {...props} />
    },
    a({ node, ...props }: { node?: any; [key: string]: any }) {
      const href = props.href || ''

      if (href === '#access-request') {
        return (
          <a
            onClick={() => window.dispatchEvent(new CustomEvent('openAccessRequestDialog'))}
            role="button">
            {props.children}
          </a>
        )
      }
      return (
        <a
          {...props}
          target="_blank"
          rel="noopener noreferrer"
        />
      )
    },
    strong({ node, ...props }: { node?: any; [key: string]: any }) {
      return <strong {...props} />
    },
  }

  return (
    <>
      <Stack
        className={styles.answerContainer}
        tabIndex={0}>
        {isError ? (
          <div className={styles.chatMessageErrorContainer}>
            <div className={styles.chatMessageError}>
              <Stack
                horizontal
                className={styles.chatMessageErrorContent}>
                <ErrorOutlineOutlined className={styles.errorIcon} />
                <span className={styles.chatMessageErrorContent}>
                  {typeof answer.error === 'string' && answer.error}
                </span>
              </Stack>
            </div>
          </div>
        ) : (
          <Stack.Item className={styles.answerContent}>
            {parsedAnswer && (
              <div className={styles.answerText}>
                <Streamdown
                  remarkPlugins={[defaultRemarkPlugins.gfm, supersub, remarkBreaks] as any}
                  plugins={{ math } as any}
                  components={components}>
                  {SANITIZE_ANSWER
                    ? DOMPurify.sanitize(parsedAnswer?.markdownFormatText, {
                        ALLOWED_TAGS: XSSAllowTags,
                        ALLOWED_ATTR: XSSAllowAttributes,
                      })
                    : parsedAnswer?.markdownFormatText}
                </Streamdown>
              </div>
            )}
          </Stack.Item>
        )}

        {parsedAnswer?.generated_chart !== null && (
          <Stack className={styles.answerContainer}>
            <Stack.Item grow>
              <img src={`data:image/png;base64, ${parsedAnswer?.generated_chart}`} />
            </Stack.Item>
          </Stack>
        )}
        {FEEDBACK_ENABLED && answer.message_id !== undefined && (
          <div className={styles.feedbackContainer}>
            <button
              className={styles.copyButton}
              onClick={onCopyResponseClicked}
              aria-label="Copy response"
              title="Copy">
              <img
                src={isCopied ? Check : Copy}
                alt="Copy"
                aria-hidden="true"
              />
            </button>
            <img
              src={ThumbUp}
              className={
                feedbackState === FeedbackType.Positive ||
                appStateContext?.state.feedbackState[answer.message_id] === FeedbackType.Positive
                  ? styles.thumbActive
                  : styles.thumb
              }
              aria-label="Like this response"
              onClick={() => onLikeResponseClicked()}
            />
            <img
              src={ThumbDown}
              className={
                feedbackState !== FeedbackType.Positive &&
                feedbackState !== FeedbackType.Neutral &&
                feedbackState !== undefined
                  ? styles.thumbActive
                  : styles.thumb
              }
              aria-label="Dislike this response"
              onClick={() => onDislikeResponseClicked()}
            />
          </div>
        )}
        {sources && (
          <div className={styles.sourcesContainer}>
            <div className={styles.sourcesHeader}>
              <span className={styles.sourcesHeaderText}>{`Sources (${sources.length})`}</span>
            </div>
            <div className={styles.sourcesList}>
              {sources.map(source => (
                <div
                  key={source}
                  className={styles.sourcesItem}>
                  <ReactMarkdown
                    remarkPlugins={[remarkBreaks]}
                    rehypePlugins={[rehypeSanitize]}
                    components={{
                      a: ({ node, ...props }) => (
                        <a
                          {...props}
                          rel="noopener noreferrer"
                          target="_blank"
                        />
                      ),
                    }}
                    className={styles.sourceText}>
                    {source}
                  </ReactMarkdown>
                </div>
              ))}
            </div>
          </div>
        )}
        {questions && (
          <div className={styles.questionsContainer}>
            <div className={styles.questionsHeader}>
              <ForumOutlined />
              <span className={styles.questionsHeaderText}>Follow Up questions</span>
            </div>
            <div>
              {questions.map(question => (
                <div
                  key={question}
                  className={styles.question}>
                  <span
                    className={styles.questionText}
                    onClick={() => onQuestionClicked?.(question)}>
                    {question}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <Stack
          horizontal
          className={styles.answerFooter}>
          {!!parsedAnswer?.citations.length && (
            <Stack.Item onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? toggleIsRefAccordionOpen() : null)}>
              <Stack style={{ width: '100%' }}>
                <Stack
                  horizontal
                  horizontalAlign="start"
                  verticalAlign="center">
                  <Text
                    className={styles.accordionTitle}
                    onClick={toggleIsRefAccordionOpen}
                    aria-label="Open references"
                    tabIndex={0}
                    role="button">
                    <span>
                      {parsedAnswer.citations.length > 1
                        ? parsedAnswer.citations.length + ' references'
                        : '1 reference'}
                    </span>
                  </Text>
                  <FontIcon
                    className={styles.accordionIcon}
                    onClick={handleChevronClick}
                    iconName={chevronIsExpanded ? 'ChevronDown' : 'ChevronRight'}
                  />
                </Stack>
              </Stack>
            </Stack.Item>
          )}
        </Stack>
        {chevronIsExpanded && (
          <div className={styles.citationWrapper}>
            {parsedAnswer?.citations.map((citation, idx) => {
              return (
                <span
                  title={createCitationFilepath(citation, ++idx)}
                  tabIndex={0}
                  role="link"
                  key={idx}
                  onClick={() => onCitationClicked(citation)}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? onCitationClicked(citation) : null)}
                  className={styles.citationContainer}
                  aria-label={createCitationFilepath(citation, idx)}>
                  <div className={styles.citation}>{idx}</div>
                  {createCitationFilepath(citation, idx, true)}
                </span>
              )
            })}
          </div>
        )}
      </Stack>

      <CustomDialog
        open={isFeedbackDialogOpen}
        setClose={async () => {
          resetFeedbackDialog()
          await onDislikeResponseClicked()
        }}
        primaryAction={() => {
          onSubmitNegativeFeedback()
        }}
        primaryActionDisabled={isEmpty(negativeFeedbackList) && isEmpty(feedbackText)}
        primaryActionButtonText="Submit"
        primaryActionLarge={true}
        title="Submit Feedback"
        keepMounted
        disclaimer="By pressing submit, your feedback will be visible to the application owner.">
        <div className={styles.feedbackDialogContent}>
          <p>Your feedback is important to improve your future experience. Why wasn't this response helpful?</p>

          <UnhelpfulFeedbackContent
            negativeFeedbackList={negativeFeedbackList}
            feedbackText={feedbackText}
            setFeedbackText={setFeedbackText}
            updateFeedbackList={updateFeedbackList}
          />
        </div>
      </CustomDialog>
    </>
  )
}
